import { ArticleComponentResizer } from "@ext/article/Components/ArticleComponentResizer";
import { NodeViewContextableWrapper } from "@ext/markdown/core/element/NodeViewContextableWrapper";
import { BlockCaption } from "@ext/markdown/elements/article/edit/helpers/BlockCaption";
import { useBlockToolbar } from "@ext/markdown/elements/article/edit/helpers/useBlockToolbar";
import VideoActions from "@ext/markdown/elements/video/edit/components/VideoActions";
import usePlayerActivation from "@ext/markdown/elements/video/edit/logic/usePlayerActivation";
import getVideoLayout from "@ext/markdown/elements/video/logic/getVideoLayout";
import { getVideoResizerClassName } from "@ext/markdown/elements/video/render/components/videoComponentStyles";
import type { NodeViewProps } from "@tiptap/react";
import type { Attrs } from "prosemirror-model";
import { type ReactElement, useCallback, useRef, useState } from "react";
import Video from "../../render/components/Video";

const EditVideo = (props: NodeViewProps): ReactElement => {
	const { editor, node, getPos, selected } = props;
	const isEditable = editor.isEditable;
	const hoverElement = useRef<HTMLDivElement>(null);
	const signatureRef = useRef<HTMLInputElement>(null);
	const [hasSignature, setHasSignature] = useState(isEditable && node.attrs?.title?.length > 0);

	const updateAttributes = useCallback(
		(attributes: Attrs) => {
			const tr = editor.view.state.tr;
			const pos = getPos();

			Object.keys(attributes).forEach((key) => {
				tr.setNodeAttribute(pos, key, attributes[key]);
			});

			editor.view.dispatch(tr);
		},
		[editor, getPos],
	);

	const saveResize = (resize: string) => {
		updateAttributes({ scale: resize });
	};

	const layout = getVideoLayout(node.attrs.path);
	const { playerState, onMouseDown, onClick } = usePlayerActivation(selected);

	useBlockToolbar(
		props,
		isEditable && (
			<VideoActions
				hasSignature={hasSignature}
				node={node}
				setHasSignature={setHasSignature}
				signatureRef={signatureRef}
				updateAttributes={updateAttributes}
			/>
		),
		{ comment: true },
	);

	return (
		<NodeViewContextableWrapper
			data-component="video"
			data-drag-handle
			data-resize-container
			data-video-player={playerState}
			draggable={true}
			onClick={onClick}
			onMouseDown={onMouseDown}
			props={props}
			ref={hoverElement}
		>
			<ArticleComponentResizer
				className={getVideoResizerClassName(layout)}
				defaultScale={layout === "vertical" ? undefined : "100%"}
				disabled={!isEditable}
				onChange={saveResize}
				scale={node.attrs.scale}
				selected={selected}
			>
				<>
					<Video
						commentId={node.attrs.comment?.id}
						inEditor
						noEm={isEditable}
						path={node.attrs.path}
						scale={node.attrs.scale}
						title={node.attrs.title}
					/>
					<BlockCaption
						editor={editor}
						getPos={getPos}
						setVisible={setHasSignature}
						signatureRef={signatureRef}
						text={node.attrs.title}
						updateAttributes={updateAttributes}
						visible={hasSignature}
					/>
				</>
			</ArticleComponentResizer>
		</NodeViewContextableWrapper>
	);
};
export default EditVideo;
